import { useState } from "react";
import { Link } from "react-router";
import { EXTERNAL_CALL_URL_MAX, ExternalCallUrl, externalCallHost } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { CopyButton } from "../shell/CopyLink.tsx";
import { OPEN_GROUP_SCHEDULE } from "./group-text.ts";

/**
 * Внешняя ссылка на созвон: показ и правка. Режим сосуществования, раздел 7
 * `docs/switching-costs.md`.
 *
 * Продукт обязан работать у преподавателя, который продолжает вести уроки в
 * Zoom, Телемосте или Meet: его занятие живёт в нашем расписании, а созвон в
 * его собственном сервисе.
 *
 * ⚠️ **Ссылка НЕ поражается в правах, и это правило, а не пожелание.** Она
 * стоит в том же ряду и той же кнопкой (`btn`), что и вход в нашу комнату, не
 * прячется в меню, не делается мельче и не сопровождается уговорами перейти к
 * нам. Соблазн сделать её на полшага неудобнее разобран в разделе 7 и запрещён
 * решением владельца 20.08.2026: так теряется не комната, а расписание.
 * Вытеснение приходит от тяжести данных, а не от трения.
 *
 * ⚠️ **Домен рядом со ссылкой обязателен.** Белого списка доменов у нас нет
 * намеренно (он сломался бы на первом же корпоративном Zoom), поэтому домен это
 * единственное, что мы про чужой адрес говорим. Читает эту подпись в том числе
 * несовершеннолетний ученик и его родитель.
 *
 * ⚠️ **Исключение ровно одно, и заведено оно 17.09.2026: ячейка действий строки
 * списка ниже порога колонок (1024 px).** Там правилом `styles.css` спрятаны и
 * домен, и копирование, а причина счётная: «Созвон», домен, копирование,
 * «Войти» и чат это 219 px в один ряд при 356 px всей строки на 390, то есть
 * ячейка переносится и строка списка вырастает вдвое. Домен самый длинный из
 * пятерых и единственный, кто не цель нажатия; копирование ушло следом, потому
 * что без него ряд впервые сходится.
 *
 * Разбираться с адресом человек идёт в два места, и оба целы: карточка занятия
 * рисует ссылку этим же компонентом, а у редактора серии разметка своя (поле с
 * адресом целиком, копирование рядом и домен подписью «Ведёт на …»), и правило
 * его не касается. Цена названа прямо: у урока, которого нет в расписании,
 * карточки занятия не существует, и чужой адрес на узком экране не виден и не
 * копируется нигде, кроме самой ссылки.
 */

/**
 * Ссылка, как её видят обе стороны.
 *
 * Ничего не рисуется, если адрес не разбирается: домен показать нечем, а
 * кликабельный адрес без единого слова о том, куда он ведёт, это ровно то, чего
 * не должно быть. Записать такой адрес нельзя (`ExternalCallUrl` его отвергает),
 * так что этот случай возможен только у строки, попавшей в базу мимо ручек.
 *
 * ⚠️ **Копирование стоит рядом, потому что нажать на ссылку это не единственное,
 * что с ней делают.** Адрес созвона пересылают: в переписку с родителем, в
 * заметку на телефоне, в чужой календарь. Без кнопки его добывали контекстным
 * меню браузера, то есть мимо продукта, а на телефоне и вовсе никак. Значок, а
 * не слово: рядом уже стоят «Созвон» и домен, и третье слово в строке карточки
 * съело бы место у них (раздел 8б `docs/cabinet-tables-design.md`).
 *
 * ⚠️ **Зовущих мест ДВА, и значок проверен для обоих** (запись 03.09.2026, до
 * неё второе место было побочным следствием, а не решением). Кроме карточки
 * занятия ссылку рисует строка урока в разделе «Уроки»
 * (`features/lessons/LessonRow.tsx`), и там довод тот же, только сильнее: в
 * ячейке действий уже стоят «Созвон», домен, «Войти» и «Ещё», и пятое слово
 * ушло бы переносом на вторую строку. Роста строки на ощупь значок не добавляет:
 * правило крупной цели (`@media (pointer: coarse)`) растит до 2.75rem и соседний
 * `.btn`, то есть высоту задаёт «Войти», а не он. Отказ буфера обмена тупика там
 * тоже не даёт: `CopyButton` в этом случае показывает сам адрес.
 *
 * ⚠️ **Абзац выше описывает ШИРОКУЮ раскладку, и с 17.09.2026 это надо читать
 * буквально.** Ниже порога колонок в той же ячейке нет ни домена, ни значка
 * копирования (исключение абзацем выше), то есть счёт «уже стоят четверо»
 * верен только там, где помещаются все.
 */
export function ExternalCallLink({ url }: { url: string | null | undefined }) {
  const host = externalCallHost(url);
  if (url === null || url === undefined || host === null) return null;

  return (
    <span className="call">
      {/*
        ⚠️ `rel="noopener noreferrer"` обязателен: без `noopener` открытая
        вкладка получает `window.opener` и может увести нашу страницу куда
        угодно, а `noreferrer` не отдаёт чужому сервису адрес нашего экрана
      */}
      <a className="btn" href={url} target="_blank" rel="noopener noreferrer">
        Созвон
      </a>
      <span className="muted call__host">{host}</span>
      <CopyButton url={url} label="Скопировать ссылку на созвон" />
    </span>
  );
}

/**
 * Правка ссылки У ЗАНЯТИЯ. Только преподаватель: ученику ручка отвечает 404.
 *
 * ⚠️ Форма правит СВОЮ ссылку урока, а не разрешённую. Поэтому пустое поле у
 * занятия серии означает «как у серии», а не «убрать созвон»: заполнив поле
 * унаследованной ссылкой, первое же сохранение превратило бы наследование в
 * копию, и правка серии перестала бы доезжать до занятия молча. Различие
 * приезжает с сервера полем `externalCallOwn`.
 *
 * ⚠️ Правится урок, а не занятие, и у ещё не подготовленного занятия править
 * нечего. Колонки в `schedule_occurrence_override` нет намеренно, разбор в
 * шапке миграции `1788393600000_external_call_url`. Здесь об этом говорится
 * словами вместо поля, которое не сработало бы.
 */
export function ExternalCallForm({
  lessonId,
  url,
  own,
  seriesId,
  group,
  groupId,
  busy,
  run,
  onDone,
}: {
  /** `null` означает «занятие ещё не подготовлено», править нечего. */
  lessonId: string | null;
  /** Уже разрешённая ссылка: своя урока либо унаследованная от серии. */
  url: string | null;
  /** Ссылка принадлежит самому занятию, а не серии. */
  own: boolean;
  /** Серия занятия. Есть она, значит пустое поле означает «как у серии». */
  seriesId: string | null;
  /**
   * Занятие группы, а не пары.
   *
   * ⚠️ Отдельным признаком, а не выводом из пустоты соседей: разбор у первой
   * ветки ниже.
   */
  group: boolean;
  /** Группа занятия, когда она известна. Ею открывается расписание группы. */
  groupId: string | null;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  onDone: () => void;
}) {
  // В поле только СВОЯ ссылка. Унаследованная показывается подписью ниже:
  // подставив её сюда, мы бы предложили человеку скопировать её себе
  const [value, setValue] = useState(own ? (url ?? "") : "");
  const [local, setLocal] = useState<string | null>(null);
  const hasSeries = seriesId !== null;

  const trimmed = value.trim();
  const host = externalCallHost(trimmed);

  function save(clear: boolean) {
    if (lessonId === null) return;
    const next = clear || trimmed === "" ? null : trimmed;
    /**
     * Проверка формы адреса ЗДЕСЬ, до отправки, и это не дублирование
     * серверной. Схема одна и та же (`ExternalCallUrl` из общего пакета),
     * просто человеку, набравшему адрес, отказ полезнее получить сразу и
     * своими словами, а не кодом `invalid_body` после круга по сети.
     */
    if (next !== null && !ExternalCallUrl.safeParse(next).success) {
      setLocal("Нужен полный адрес, начинающийся с https:// и без пробелов");
      return;
    }
    setLocal(null);
    void run(async () => {
      await api.setLessonExternalCallUrl(lessonId, next);
      onDone();
    });
  }

  /**
   * ⚠️ **Занятие ГРУППЫ: своей ссылки у него не бывает вовсе**, сколько бы
   * уроков у него ни было. Уроков там столько же, сколько пар, и правка «в
   * урок» записалась бы одному ребёнку из двенадцати, поэтому ручка отвечает
   * такому уроку `group_schedule_managed` (разбор целиком в шапке
   * `apps/api/src/routes/schedule/external-call.ts`).
   *
   * ⚠️ **Вид занятия читается полем `kind`, а НЕ пустотой `lessonId` и
   * `seriesId`.** До 02.09.2026 здесь стояла именно пустота, и она верна ровно
   * для сводной строки пучка до разбора. Мимо неё проходили две строки, и обе
   * получали форму, ведущую в отказ: сводная строка с разбором (у неё
   * появляется опорный урок, `schedule-group-bunch.ts`) и осиротевший урок
   * занятия группы во втором потоке сетки (у него есть и урок, и серия). Ровно
   * та же ошибка и ровно то же лекарство, что у фигуры занятия в
   * `EntryKind.tsx`: **отсутствие поля нельзя читать как «занятие личное»**.
   *
   * ⚠️ Дверь ведёт ПРЯМО в расписание группы, а подписывает её общая
   * `OPEN_GROUP_SCHEDULE`, та же самая, что в диалоге создания занятия и в
   * списке адресатов. Это одна дверь, и называется она одинаково во всех трёх
   * местах, откуда в неё уходят.
   *
   * Ссылки нет вовсе, когда группа неизвестна, и это тот же приём, что у
   * серии строкой ниже. Случай достижимый: у осиротевшего урока группы поля
   * `group` нет вовсе. Выдуманная дверь хуже, чем её отсутствие: адрес
   * `/schedule/groups/undefined` открывает пустой экран.
   */
  if (group) {
    return (
      <div className="note">
        <p>
          У занятия группы ссылка на созвон одна на всех, и правится она в расписании группы, там
          же, где дни и время.
        </p>
        <div className="form__foot">
          <button type="button" className="btn btn--quiet" onClick={onDone}>
            Не надо
          </button>
          {groupId !== null && (
            <Link className="btn" to={`/schedule/groups/${groupId}`}>
              {OPEN_GROUP_SCHEDULE}
            </Link>
          )}
        </div>
      </div>
    );
  }

  /**
   * Урока ещё нет, значит переопределять ссылку негде: колонка живёт на уроке.
   * Тупика здесь быть не должно, поэтому вместо «нельзя» стоит рабочий путь на
   * экран серии, где та же ссылка правится для всех её занятий сразу.
   *
   * ⚠️ Строка второго потока (разовое занятие, осиротевший урок) сюда не
   * попадает: у неё урок есть всегда, иначе её не было бы и в сетке. Значит
   * сюда доходит занятие серии, и `seriesId` у него не пуст: занятие группы
   * разобрано веткой выше.
   */
  if (lessonId === null) {
    return (
      <div className="note">
        <p>
          Сейчас у занятия действует ссылка серии. Своя появляется у подготовленного занятия:
          нажмите «Подготовить», и ссылку можно будет поменять только на этот раз.
        </p>
        <div className="form__foot">
          <button type="button" className="btn btn--quiet" onClick={onDone}>
            Не надо
          </button>
          {seriesId !== null && (
            <Link className="btn" to={`/schedule/series/${seriesId}`}>
              К серии
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="form">
      <label className="field">
        <span className="lbl">Ссылка на созвон</span>
        <input
          className="fld"
          value={value}
          maxLength={EXTERNAL_CALL_URL_MAX}
          inputMode="url"
          placeholder={hasSeries ? "как у серии" : "https://…"}
          aria-invalid={local !== null}
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      {/* ⚠️ Отказ ЗАМЕНЯЕТ подсказку, а не встаёт под ней: иначе поле с ошибкой
          выше поля без неё, и форма прыгает в момент нажатия «Сохранить» */}
      {local !== null ? (
        <p className="hint hint--err">{local}</p>
      ) : (
        <p className="hint">
          {host !== null && `Ведёт на ${host}. `}
          {hasSeries
            ? "Пустое поле означает «как у серии»."
            : "У разового занятия серии нет, поэтому ссылка живёт только здесь."}{" "}
          Ученик увидит её рядом с кнопкой входа в нашу комнату.
        </p>
      )}
      {!own && url !== null && (
        <p className="hint">Сейчас действует ссылка серии: {externalCallHost(url)}</p>
      )}

      <div className="form__foot">
        {own && (
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => save(true)}
          >
            {hasSeries ? "Вернуть как у серии" : "Убрать ссылку"}
          </button>
        )}
        <button type="button" className="btn btn--quiet" disabled={busy} onClick={onDone}>
          Не надо
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => save(false)}>
          Сохранить
        </button>
      </div>
    </div>
  );
}
